import {
  Col,
  Form,
  Row,
  Space,
  Tag,
  message
} from "antd";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useCurrentPng } from "recharts-to-png";
import { emitter } from "../../../events";
import { handlers } from '../../../helpers/helpers';
import { getCookie } from "../../../services/cookies";
import {
  assingDoctor,
  checkRxValidityExam,
  checkValidityExam,
  createExamReport,
  getLinkedExam,
  getReport,
  saveExam,
  setLinkedExamId,
  updateExamState,
  updateLaboratoryExams,
} from "../../../services/exam";
import { triggerOperativeConditionals } from '../../../services/admission';
import { removeAsset } from "../../../services/general";
import { base64ToStream, convertFile, uploadAssetController } from "../../../utils/fileUtils";
import proceduresCodes from '../../../utils/proceduresCodes.json';
import { useSharedInputs } from "../hooks/useSharedInputs";
import ExamButtons from './ExamButtons';
import LinkedExamBanner from "./LinkedExamBanner";
import VigentExam from "./VigentExam";
import VigentExamRx from "./VigentExamRx";
import BasicAudiometryForm from './formTypes/BasicAudiometryForm';
import BilateralAudiometryForm from './formTypes/BilateralAudiometryForm';
import CalculatorForm from './formTypes/CalculatorForm';
import CompleteAudiometryForm from './formTypes/CompleteAudiometryForm';
import DermatologyForm from './formTypes/DermatologyForm';
import DrugForm from "./formTypes/DrugForm";
import NormalForm from "./formTypes/NormalForm";
import LaboratoryForm from "./formTypes/LaboratoryForm";
import OdontogramForm from './formTypes/OdontogramForm';
import PsychologyForm from "./formTypes/PsychologyForm";
import QuestionCalculatorForm from "./formTypes/QuestionCalculatorForm";
import RxForm from "./formTypes/RxForm";
import groupedExams from '../../../utils/groupedExams.json';
import SimpleMusculoskeletalForm from "./formTypes/SimpleMusculoskeletalForm";
import InterconsultationForm from "./formTypes/InterconsultationForm";
import typesStyles from './utils/ExamTypes';

const audiometryCodes = {
  [proceduresCodes.audiometria["Inc. Auditiva"]]: 'IncAudi',
  [proceduresCodes.audiometria["Inc. Ganancia"]]: 'IncGan',
  [proceduresCodes.audiometria["average_oi_500_to_2000"]]: '500_2000OI',
  [proceduresCodes.audiometria["average_od_500_to_2000"]]: '500_2000OD',
  [proceduresCodes.audiometria["average_oi_1000_to_6000"]]: '1000_6000OI',
  [proceduresCodes.audiometria["average_od_1000_to_6000"]]: '1000_6000OD',
};

const psychologyCalculatorFormTypes = [
  'psicologico', 'psicologico_dsm_v_bf', 'sueño', 'pid_5', 'pid_5_type_2', 'plutchik',
  'plutchik_ra', 'barratt', 'rotter', 'epq_a', 'epworth', 'epworth_motorized', 'das21',
  'lake_louise', 'acrophobiaCohen', 'confinedSpaces', 'cage', 'audit',
  'organizational_stress', 'yoshitake', 'work_satisfaction', 'long_mbi',
  'factor_n', 'factor_v', 'factor_r', 'anxiety_zung', 'depression_zung',
  'anxiety_goldberg', 'depression_goldberg', 'stress_seppo_aro', 'equal_pairs_4',
  'missing_objects', 'wonderlic_a', 'raven', 'buss_perry', 'idare', 'bfq', 'mlq_5x', 'vocal_handicap',
]

const allCalculatorFormTypes = [
  'audiometria', 'framingham', 'framingham_cox', 'psicologico', 'psicologico_dsm_v_bf', 'sueño', 'pid_5', 'pid_5_type_2',
  'plutchik', 'plutchik_ra', 'barratt', 'rotter', 'epq_a', 'epworth', 'epworth_motorized', 'das21', 'lake_louise',
  'acrophobiaCohen', 'confinedSpaces', 'cage', 'audit', 'organizational_stress', 'yoshitake',
  'work_satisfaction', 'long_mbi', 'factor_n', 'factor_v', 'factor_r', 'anxiety_zung', 'depression_zung',
  'anxiety_goldberg', 'depression_goldberg', 'stress_seppo_aro', 'equal_pairs_4', 'missing_objects', 'wonderlic_a', 'raven',
  'mlq_5x', 'buss_perry', 'idare', 'bfq', 'vocal_handicap'
]

const normalFormTypes = [
  'normal'
]

const questionCalculatorFormTypes = [
  'respiratory_symptoms'
]

const signatureVigentExamStates = [
  'created', 'occupied', 'inProgress', 'done'
]

const ExamInputs = forwardRef(({
  inputs,
  patient,
  disableForm,
  readOnly = false,
  admissionPatientExamId,
  drugModal,
  setDrugModal,
  fetchInputs,
  toDoctor,
  admissionPatientId,
  fetchMessage,
  loading,
  setLoading,
  setRefresh,
  fromExternal,
  from,
  navigate,
  location,
  examsTabs,
  isPermittedRole,
  fetchExamsTabs,
  isGroupedExams,
  isLast,
  countryCode,
  allExams,
  handleSaveAllExams,
  handleFinishAllExams,
  getTranslationKey,
  enableRx,
  enableEcg,
  refreshMedicalConsultationDetails,
  countrySettings,
  allExamsInfo: consultationExamsData,
  setAllExamsInfo: setConsultationExamsData,
  consultationServices,
  groupType,
  laboratoryCategoryInfo,
}, examRef) => {
  countryCode = countryCode || getCookie('country_code');
  const vigentExamEnabled = countrySettings?.vigentExam?.[countryCode];
  const isSignatureView = from === 'expert';
  const canUseVigentExam = !readOnly && (!fromExternal || toDoctor || isSignatureView)
    && (
      isSignatureView
        ? signatureVigentExamStates.includes(inputs.state)
        : (inputs.state === 'occupied' || inputs.state === 'created')
    );
  const [vigentExam, setVigentExam] = useState();
  const [linkedExam, setLinkedExam] = useState(null);
  const [isVinculating, setIsVinculating] = useState(false);
  const [vigentExamResultIds, setVigentExamResultIds] = useState(new Set());
  const [loadingFiles, setLoadingFiles] = useState({});
  const [fileList, setFileList] = useState({});
  const [getPng, { ref }] = useCurrentPng();
  const [getPngRE, { ref: refRE }] = useCurrentPng();
  const [getPngLE, { ref: refLE }] = useCurrentPng();
  const [form] = Form.useForm();
  useSharedInputs({
    fromExternal,
    consultationExamsData,
    setConsultationExamsData,
    consultationServices,
    admissionPatientId,
    isGroupedExams,
    allExams,
    inputs,
    form,
    from
  });
  const [audiometryState, setAudiometry] = useState({
    'extra_data': {
      'frecuencies_od': '',
      'frecuencies_id': '',
    },
    '500_2000OI': '0.0',
    '500_2000OD': '0.0',
    '1000_6000OD': '0.0',
    '1000_6000OI': '0.0',
    IncGan: '0.0',
    IncAudi: '0.0',
  });
  const [psychoState, setPsycho] = useState({});
  const [drugState, setDrug] = useState([]);
  const [calculatorState, setCalculatorState] = useState([]);
  const [calculatorData, setCalculatorData] = useState(null);
  const [convertError, setConvertError] = useState(false);
  const [imageUrl, setImageUrl] = useState({});
  const roles = getCookie('roles');
  const showAudiometryChartRef = useRef(null);
  const dermatologyChartRef = useRef(null);
  const [dermatologyState, setDermatologyState] = useState({
    marks: [],
    otherText: '',
  });
  const odontogramChartRef = useRef(null);
  const [odontogramState, setOdontogramState] = useState(
    () => inputs.extra_data?.odontogram || { toothFindings: {}, multiToothFindings: [] }
  );


  const getFiles = useCallback(async () => {
    const procedureFiles = inputs.procedures.filter(procedure => procedure.input_type === 'file' && procedure.result_id);
    if (procedureFiles.length) {
      setLoadingFiles(procedureFiles.reduce((prev, acc) => ({ ...prev, [acc.id]: true }), []));

      const files = procedureFiles
        .map(procedure => ({
          id: procedure.id,
          name: procedure.document_file_name,
          file: {
            field: 'document',
            id: procedure.result_id,
            document_type: 'AdmissionProcedureResult',
            type: procedure.document_content_type,
          },
        })
        );
      setFileList(fileList => ({
        ...fileList,
        ...files.reduce((prev, acc) => ({
          ...prev,
          ...(!!acc.name && {
            [acc.id]: [{
              uid: '-1',
              name: acc.name || 'archivo',
              status: 'done',
              ...acc.file,
            }]
          })
        }), {}),
      }));

      setLoadingFiles(procedureFiles.reduce((prev, acc) => ({ ...prev, [acc.id]: false }), []));
    }
  }, [inputs.procedures]);

  const checkVigentExam = useCallback(async () => {
    try {
      if ((inputs.form_type === 'rx' && !enableRx) || isGroupedExams) {
        return;
      }

      const isRxExam = inputs.form_type === 'rx' && !!enableRx;
      if (!isRxExam && !vigentExamEnabled) return;

      const validationService = isRxExam ? checkRxValidityExam : checkValidityExam;

      const response = await validationService({
        patient_id: patient.patient_id,
        exam_id: inputs.id,
        admissionPatientId,
      });

      if (response) {
        if (isRxExam) {
          setVigentExam({
            id: response.admissionPatientExamId,
            rxUID: response.RxUID,
            woid: response.woid,
            branchOffice: response.branchOffice,
            dcmDate: response.dcm_date,
            validityCriterion: response.validityCriterion,
            validationDate: response.validationDate
          });
        } else {
          setVigentExam({
            id: response.id,
            examDate: response.examDate,
            branchOffice: response.branchOffice,
            validityCriterion: response.validityCriterion,
            validationDate: response.validationDate,
          });
        }
      }
    } catch (error) {
      console.error('Error', error);
      message.error({
        content: 'Error al buscar examen vigente',
        style: {
          marginTop: '45vh',
        }
      });
    }
  }, [inputs?.id, inputs?.form_type, patient?.patient_id, enableRx, admissionPatientId, isGroupedExams, vigentExamEnabled])

  useEffect(() => {
    if (inputs.procedures) {
      getFiles();
    }
  }, [getFiles]);

  useEffect(() => {
    if (canUseVigentExam && inputs?.id && patient?.patient_id && admissionPatientId && (inputs.form_type !== 'rx' || enableRx !== undefined)) {
      checkVigentExam();
    }
  }, [checkVigentExam, inputs?.id, patient?.patient_id, admissionPatientId, inputs.form_type, enableRx, canUseVigentExam]);

  const checkLinkedExam = useCallback(async () => {
    try {
      const response = await getLinkedExam({ admissionPatientExamId });
      setLinkedExam(response);
    } catch (error) {
      console.error('Error fetching linked exam', error);
      message.error({ content: 'Error al obtener examen vinculado', style: { marginTop: '45vh' } });
    }
  }, [admissionPatientExamId]);

  useEffect(() => {
    if (admissionPatientExamId && !isGroupedExams && vigentExamEnabled) {
      checkLinkedExam();
    }
  }, [checkLinkedExam, admissionPatientExamId, isGroupedExams, vigentExamEnabled]);

  useEffect(() => {
    const handler = ({ data: results }) => {
      console.log('Received lab data:', results);

      const newFormValues = { procedures: {} };
      for (const result of results) {
        newFormValues.procedures[result.id] = result.result;
      }

      console.log('New form values:', newFormValues);
      form.setFieldsValue(newFormValues);
    };

    emitter.on('labData', handler);
    return () => emitter.off('labData', handler);
  }, [form]);

  const handleConvertFile = useCallback(async (procedure, file, url) => {
    setLoading(state => ({ ...state, save: true }));
    setConvertError(false);
    try {
      if (procedure && file) {
        const response = await convertFile({
          file: file,
          type: procedure.document_content_type,
          url,
        });
        if (response) {
          const convertedImageProcedure = inputs.procedures.find(inputProcedure => inputProcedure.code?.includes(`IMAGE_${procedure.code.split('_')[1]}`))
          if (convertedImageProcedure) {
            setFileList(
              list => ({ ...list, [convertedImageProcedure.id]: [response] })
            )
          }
        }
      }
    } catch (error) {
      console.error('Error', error);
      fetchMessage('error', 'Error al convertir el archivo');
      setConvertError(true);
    } finally {
      setLoading(state => ({ ...state, save: false }));
    }
  }, [fetchMessage, inputs.procedures, setLoading]);

  const save = useCallback(async (values, isDoctor = false) => {
    try {
      const results = {
        procedures: [],
        measurements: [],
        questions: [],
      };
      const blockedFilesIds = {};
      Object.keys(results).forEach(examType => {
        if (inputs[examType]) {
          inputs[examType]
            .filter(item => {
              const procedureCondition = (isDoctor && item.medical_visibility);
              const isPsychologist = roles.includes('equipo_psicologia');
              const restCondition = (toDoctor && examType !== 'procedures');
              const isVocalHandicapDoctor = fromExternal && isDoctor && inputs.form_type === 'vocal_handicap';
              return !item.blocked && ((procedureCondition || restCondition) || (!isDoctor || isPsychologist) || (fromExternal && countryCode === 'CL') || isVocalHandicapDoctor);
            })
            .sort((first, second) => first.order - second.order)
            .forEach(item => {
              const result = values[examType] ? values[examType][item.id] : null;
              let finalResult = result;
              if (item.input_type === 'date' && result && moment.isMoment(result)) {
                finalResult = result.format('DD-MM-YYYY');
              }
              if (item.input_type === 'time' && result && moment.isMoment(result)) {
                finalResult = result.format('HH:mm');
              }

              if (item.input_type === 'select_multiple' && Array.isArray(result)) {
                finalResult = result.join(',');
              }
              if ((fileList[item.id] && fileList[item.id][0] && !fileList[item.id][0].url) || !fileList[item.id]) {
                const obj = {
                  [`${examType.slice(0, -1)}_id`]: item.id,
                  [`${examType.slice(0, -1)}_order`]: item.order,
                  [`admission_${examType.slice(0, -1)}_result_id`]: item.result_id,
                }
                if (examType === 'questions') {
                  obj.answer = finalResult ?? ' ';
                  obj.questionOptions = item.options;
                  obj.survey_code = item.survey_code;
                } else {
                  obj.result = finalResult ?? ' ';
                }
                obj.code = item.code;
                results[examType].push(obj);
              }
            });
        }
      });
      if (inputs.form_type === 'audiometria') {
        inputs.procedures.forEach(item => {
          const obj = {
            procedure_id: item.id,
            procedure_order: item.order,
            admission_procedure_result_id: item.result_id,
          };

          if (audiometryCodes[item.code]) {
            results.procedures.push({
              ...obj,
              result: audiometryState[audiometryCodes[item.code]],
            })
          }
          if (item.code === proceduresCodes.audiometria["archivo"][countryCode]) {
            results.procedures.push({
              ...obj,
              result: null,
            });
            blockedFilesIds[item.id] = 'chart';
          }
        });
      } else if (inputs.form_type === 'audiometria_bilateral') {
        inputs.procedures.forEach(item => {
          const obj = {
            procedure_id: item.id,
            procedure_order: item.order,
            admission_procedure_result_id: item.result_id,
          };

          const codeOD = proceduresCodes.audiometria["archivo_od"]?.[countryCode];
          const codeOI = proceduresCodes.audiometria["archivo_oi"]?.[countryCode];
          const codeStandard = proceduresCodes.audiometria["archivo"]?.[countryCode];

          const audiometryChartBlockedFiles = {
            [codeOD]: 'chart_re',
            [codeOI]: 'chart_le',
            [codeStandard]: 'chart'
          };
          const chartKey = audiometryChartBlockedFiles[item.code];
          if (chartKey) {
            results.procedures.push({ ...obj, result: null });
            blockedFilesIds[item.id] = chartKey;
          }
        });
      } else if (psychologyCalculatorFormTypes.includes(inputs.form_type)) {
        results.procedures = [
          ...results.procedures,
          ...Object.values(psychoState)
            .filter((state) => !results.procedures.find(procedure => procedure.procedure_id === state.id))
            .sort((first, second) => first.order - second.order)
            .map(procedure => ({
              procedure_id: procedure.id,
              procedure_order: procedure.order,
              admission_procedure_result_id: procedure.result_id
            })),
        ]
      } else if (inputs.form_type === 'drug_test' && drugState.length) {
        const sampleProcedure = inputs.procedures.find(procedure => procedure.blocked);

        results.procedures.push({
          procedure_id: sampleProcedure.id,
          procedure_order: sampleProcedure.order,
          admission_procedure_result_id: sampleProcedure.result_id,
          result: null,
        })

        blockedFilesIds[sampleProcedure.id] = drugState[0];
      } else if ([
        'framingham',
        'framingham_cox'
      ].includes(inputs.form_type)) {
        const calculatorProcedures =
          Object.values(calculatorState)
            .filter((state) => !results.procedures.find(procedure => procedure.procedure_id === state.id))
            .sort((first, second) => first.order - second.order)
            .map(procedure => ({
              procedure_id: procedure.id,
              procedure_order: procedure.order,
              admission_procedure_result_id: procedure.result_id
            }));
        results.procedures = [
          ...results.procedures,
          ...calculatorProcedures,
        ]
      } else if (inputs.form_type === 'dermatology') {
        const dermaCodes = proceduresCodes.dermatology;
        const countCodes = Object.values(dermaCodes).filter(code => code.startsWith('COUNT_'));

        inputs.procedures.forEach(item => {
          const obj = {
            procedure_id: item.id,
            procedure_order: item.order,
            admission_procedure_result_id: item.result_id,
          };

          if (countCodes.includes(item.code)) {
            results.procedures.push({
              ...obj,
              result: values.procedures?.[item.id],
            });
          }
          if (item.code === dermaCodes?.chart) {
            results.procedures.push({ ...obj, result: null });
            blockedFilesIds[item.id] = 'dermatology_chart';
          }
        });
      } else if (inputs.form_type === 'dental') {
        inputs.procedures.forEach(item => {
          const obj = {
            procedure_id: item.id,
            procedure_order: item.order,
            admission_procedure_result_id: item.result_id,
          };

          if (item.code === proceduresCodes.dental.chart) {
            results.procedures.push({ ...obj, result: null });
            blockedFilesIds[item.id] = 'odontogram_chart';
          }
        });
      }

      const [response] = await Promise.all(
        Object.entries(results).map(async ([examType, value]) => {
          if (value.length > 0) {
            return saveExam({
              body: {
                form_type: inputs.form_type,
                data: calculatorData,
                [examType]: value,
                ...(examType === 'procedures' && { questions: results.questions }),
                ...(inputs.form_type === 'audiometria' && { extra_data: audiometryState.extra_data }),
                ...(inputs.form_type === 'dermatology' && {
                  extra_data: { facialExamMarks: JSON.stringify({ marks: dermatologyState.marks }) },
                }),
                ...(examType === 'procedures' && inputs.form_type === 'dental' && {
                  extra_data: { odontogram: odontogramState },
                }),
              },
              type: examType,
              admissionPatientExamId
            });
          }
          return null;
        })
      );
      const fileResponse = await Promise.all(
        Object.keys({
          ...fileList,
          ...blockedFilesIds
        })
          .map(async id => {
            let file;
            if (response) {
              const result = response.find(item => item && item.length > 0 && item[0].procedure_id.toString() === id);
              if (result && result.length > 0 && result[0].result_id) {
                const procedure = inputs.procedures && inputs.procedures.find(item => item.id.toString() === id);
                if (procedure && procedure.code === proceduresCodes.audiometria["archivo"]?.[countryCode] && ref?.current) {
                  const base64 = await getPng();
                  file = base64ToStream(base64, 'image/png', 'chart.png');
                } else if (procedure && proceduresCodes.audiometria["archivo_od"]?.[countryCode] && procedure.code === proceduresCodes.audiometria["archivo_od"][countryCode] && refRE?.current) {
                  const base64 = await getPngRE();
                  file = base64ToStream(base64, 'image/png', 'chart_re.png');
                } else if (procedure && procedure.code === proceduresCodes.dermatology.chart && dermatologyChartRef?.current) {
                  file = await dermatologyChartRef.current();
                } else if (procedure && procedure.code === proceduresCodes.dental.chart && odontogramChartRef?.current) {
                  const base64 = await odontogramChartRef.current.captureNormativeImage();
                  if (base64) {
                    file = base64ToStream(base64, 'image/png', 'odontograma.png');
                  }
                } else if (procedure && proceduresCodes.audiometria["archivo_oi"]?.[countryCode] && procedure.code === proceduresCodes.audiometria["archivo_oi"][countryCode] && refLE?.current) {
                  const base64 = await getPngLE();
                  file = base64ToStream(base64, 'image/png', 'chart_le.png');
                } else if (inputs.form_type === 'drug_test') {
                  file = blockedFilesIds[id];
                } else {
                  file = fileList[id]?.[0];
                }
                if (file && (!file.uid || file?.uid !== '-1')) {
                  return await uploadAssetController({
                    file,
                    object_field: 'document',
                    object_id: result[0].result_id,
                    object_type: 'AdmissionProcedureResult',
                  })
                }
              }
            }
            return undefined;
          })
      );

      if (fileResponse && fileResponse.length > 0 && fileResponse.every(file => file && file.document_url)) {
        fetchMessage('success', 'Documento cargado correctamente');
      }

      if ((['audiometria', 'audiometria_bilateral'].includes(inputs.form_type)) && showAudiometryChartRef.current) {
        showAudiometryChartRef.current();
      }
    } catch (error) {
      console.error('Error!', error);
      throw (error);
    }
  }, [admissionPatientExamId, audiometryState, calculatorData, calculatorState, countryCode, drugState, fetchMessage, fileList, fromExternal, getPng, inputs, odontogramState, psychoState, ref, roles, toDoctor]);

  const triggerConditionalsIfNeeded = async () => {
    if (isGroupedExams) return;

    const triggerConditionals = countrySettings?.saveExams?.triggerOperativeConditionals?.[countryCode];
    if (triggerConditionals && admissionPatientId) {
      try {
        await triggerOperativeConditionals({ admissionPatientId });
      } catch (err) {
        console.error('Error triggering operative conditionals', err);
      }
    }
  };

  const linkVigentExam = useCallback(async () => {
    if (vigentExamResultIds.size > 0 && vigentExam?.id) {
      try {
        await setLinkedExamId({ admissionPatientExamId, linkedExamId: vigentExam.id });
        setLinkedExam({
          examDate: vigentExam.examDate,
          branchOffice: vigentExam.branchOffice,
          validityCriterion: vigentExam.validityCriterion,
          validationDate: vigentExam.validationDate,
        });
      } catch (err) {
        console.error('Error linking vigent exam', err);
        message.error({ content: 'Error al vincular examen vigente', style: { marginTop: '45vh' } });
      }
    }
  }, [admissionPatientExamId, vigentExam, vigentExamResultIds]);

  const handleSave = useCallback(async () => {
    if (readOnly) return;
    const values = form.getFieldsValue();
    try {
      if (!isGroupedExams) {
        setLoading(state => ({ ...state, save: true }));
      }

      await save(values);
      await Promise.all([
        updateExamState({
          admissionPatientExamId,
          exam_state: fromExternal ? 'done' : 'inProgress',
          ...(!fromExternal && inputs.state === 'done' && { only_update_save_admin: true }),
          applyExpertAutoFill: fromExternal ? false : true,
        }),
        triggerConditionalsIfNeeded()
      ]);

      try {
        await linkVigentExam();
      } catch (err) {
        console.error('Error linking vigent exam', err);
        message.error({ content: 'Error al vincular examen vigente', style: { marginTop: '45vh' } });
      }
      setVigentExamResultIds(new Set());

      if (!isGroupedExams) {
        await fetchInputs();
      }

      if (!fromExternal && refreshMedicalConsultationDetails) {
        refreshMedicalConsultationDetails();
      }
    } catch (error) {
      message.error({
        content: 'Error al guardar el examen',
        style: {
          marginTop: '45vh',
        }
      });
      console.error('Error', error);
    } finally {
      if (!isGroupedExams) {
        setLoading(state => ({ ...state, save: false }));
      }
    }
  }, [admissionPatientExamId, readOnly, fetchInputs, form, fromExternal, inputs.state, isGroupedExams, refreshMedicalConsultationDetails, save, setLoading, countrySettings, countryCode, admissionPatientId, triggerConditionalsIfNeeded, linkVigentExam]);

  const handleDoctorSave = useCallback(async () => {
    if (readOnly) return;
    const values = form.getFieldsValue();
    try {
      if (!isGroupedExams) {
        setLoading(state => ({ ...state, save: true }));
      }
      await save(values, true);
      await Promise.all([
        updateExamState({
          admissionPatientExamId,
          exam_state: 'done',
        }),
        triggerConditionalsIfNeeded()
      ]);

      await updateExamState({
        admissionPatientExamId,
        exam_state: 'done',
        applyExpertAutoFill: !fromExternal,
      });

      try {
        await linkVigentExam();
      } catch (err) {
        console.error('Error linking vigent exam', err);
        message.error({ content: 'Error al vincular examen vigente', style: { marginTop: '45vh' } });
      }
      setVigentExamResultIds(new Set());

      if (!isGroupedExams) {
        await fetchInputs();
      }

      if (refreshMedicalConsultationDetails) {
        refreshMedicalConsultationDetails();
      }
    } catch (error) {
      message.error({
        content: 'Error al guardar el examen',
        style: {
          marginTop: '45vh',
        }
      });
      console.error('Error', error);
    } finally {
      if (!isGroupedExams) {
        setLoading(state => ({ ...state, save: false }));
      }
    }
  }, [admissionPatientExamId, readOnly, fetchInputs, form, fromExternal, isGroupedExams, refreshMedicalConsultationDetails, save, setLoading, countrySettings, countryCode, admissionPatientId, triggerConditionalsIfNeeded, linkVigentExam]);

  const onFinish = useCallback(async values => {
    if (readOnly) return;
    if (inputs.state !== 'done') {
      try {
        if (!isGroupedExams) {
          setLoading(state => ({ ...state, save: true }));
        }
        await save(values);
        await Promise.all([
          updateExamState({
            admissionPatientExamId,
            exam_state: 'done',
            applyExpertAutoFill: !fromExternal,
            ...(inputs.state === 'done' && { only_update_save_admin: true }),
          }),
          triggerConditionalsIfNeeded()
        ]);

        try {
          await linkVigentExam();
        } catch (err) {
          console.error('Error linking vigent exam', err);
          message.error({ content: 'Error al vincular examen vigente', style: { marginTop: '45vh' } });
        }
        setVigentExamResultIds(new Set());

        if (admissionPatientId) {
          try {
            await assingDoctor({ admissionPatientId, admissionPatientExamId });
          } catch (error) {
            console.error('patient not assignable', error);
          }
        }

        if (!examsTabs && !fromExternal && !isGroupedExams) {
          navigate({
            pathname: location.state ? navigate(-1) : '/examenes/buscar',
            search: location.state?.search || null
          });
        } else {
          if (!isGroupedExams) {
            await fetchInputs();
            await fetchExamsTabs({
              admission_patient_id: admissionPatientId,
            });
          }
        }
      } catch (error) {
        console.error('Error', error);
        message.error({
          content: 'Error al guardar el examen',
          style: {
            marginTop: '45vh',
          }
        });
      } finally {
        if (!isGroupedExams) {
          setLoading(state => ({ ...state, save: false }));
        }
      }
    }
  }, [admissionPatientExamId, admissionPatientId, readOnly, examsTabs, fetchExamsTabs, fetchInputs, fromExternal, inputs.state, isGroupedExams, location.state, navigate, save, setLoading, countrySettings, countryCode, triggerConditionalsIfNeeded, linkVigentExam]);

  const generateReport = async () => {
    return createExamReport({
      id: isGroupedExams ? admissionPatientId : admissionPatientExamId,
      ...(isGroupedExams && fromExternal && { admission_patient_exam_id: admissionPatientExamId }),
      ...(isGroupedExams && {
        group_type: groupType,
        group_type_name: groupedExams[groupType]
      })
    });
  }

  const downloadReport = async (id) => {
    setLoading(loading => ({ ...loading, downloading: true }));
    try {
      const response = await generateReport(id);
      handlers.base642Open(response["pdf"]);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al descargar informe de examen", style: { marginTop: "35vh", fontSize: 16, }, });
    } finally {
      setLoading(loading => ({ ...loading, downloading: false }));
    }
  };

  const status = (item, type) => {
    let result = '';
    if (['IncGan'].includes(type)) {
      const incGan = inputs.procedures?.find(procedure => procedure.code === type && procedure.result);
      result = incGan?.result_type || '';
    } else if ((item.result_type?.includes('reproves') || item.result_type?.includes('altered')) && roles?.includes('doctor')
      && (
        (['integer', 'float', 'select', 'select_multiple', 'radio', 'date', 'time'].includes(item.input_type)
          && type.input_name === 'procedures'
          && (normalFormTypes.includes(inputs.form_type) || questionCalculatorFormTypes.includes(inputs.form_type) || inputs.form_type === 'simple_musculoskeletal'))
        || type.input_name === 'measurements'
      )
    ) {
      result = item.result_type;
    }
    return result;
  }

  const updateLaboratoryExamsButton = async () => {
    setLoading(state => ({ ...state, finish: true }));
    try {
      const response = await updateLaboratoryExams({ admissionPatientId, state: 'done' })
      if (response) {
        message.success({
          content: "Exámenes actualizados correctamente",
          style: {
            marginTop: "35vh",
            fontSize: 16,
          },
        });
        await fetchInputs();
        await fetchExamsTabs({
          admission_patient_id: admissionPatientId,
        });
      } else {
        throw new Error(response);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno: ${error}`,
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
    } finally {
      setLoading(state => ({ ...state, finish: false }));
    }
  }

  const removeDocument = async (item, list) => {
    const procedureFiles = inputs.procedures.filter(procedure => procedure.input_type === 'file' && procedure.result_id === item.id)
    setLoadingFiles(procedureFiles.reduce((prev, acc) => ({ ...prev, [acc.id]: true }), []));
    try {
      if (item.result_id) {
        const removeResult = await removeAsset({
          field: 'document',
          id: item.result_id,
          type: 'AdmissionProcedureResult',
        });
        if (removeResult) {
          list = { ...list, [item.id]: [] };
          message.success({
            content: "Exámenes actualizados correctamente",
            style: {
              marginTop: "35vh",
              fontSize: 16,
            },
          });
        }
      } else {
        list = { ...list, [item.id]: [] };
      }
    } catch (error) {
      console.error('Error', error);
      message.error({
        content: `Error interno: ${error}`,
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
    } finally {
      setLoadingFiles(procedureFiles.reduce((prev, acc) => ({ ...prev, [acc.id]: false }), []));
    }
    return list;
  }

  const handleVigentExamFileChange = useCallback((id) => {
    setVigentExamResultIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
    }
  };

  const isDICOM = (file) => {
    return (
      file?.type?.includes('dicom')
      || file?.name?.includes('.dcm')
    );
  }

  const executeConvertFile = async (file, procedure) => {
    if (procedure.code?.includes('DOCUMENT_') &&
      inputs.procedures.find(inputProcedure => inputProcedure.code?.includes(`IMAGE_${procedure.code.split('_')[1]}`)) &&
      (isDICOM(file) || ['application/pdf'].includes(file.type))) {
      setImageUrl({ ...imageUrl, [procedure.id]: null });
      handleConvertFile(procedure, file);
    }
  }

  const inputsBreakpoints = () => {
    let defaultBP = 20;

    if (allCalculatorFormTypes.includes(inputs.form_type) || questionCalculatorFormTypes.includes(inputs.form_type)) {
      defaultBP = 22;
    }

    const isDental = inputs.form_type === 'dental';
    if (isDental) {
      defaultBP = 23;
    }

    return defaultBP;
  };
  const knownFormTypes = [
    'audiometria',
    'audiometria_bilateral',
    'framingham',
    'framingham_cox',
    'drug_test',
    'rx',
    'dermatology',
    'dental',
    'simple_musculoskeletal',
    'interconsultation',
    ...psychologyCalculatorFormTypes
  ];

  useImperativeHandle(examRef, () => ({
    handleSave: () => {
      if (roles.includes('doctor') || roles.includes('direccion_medica')) {
        return handleDoctorSave();
      }
      return handleSave();
    },
    handleFinish: () => {
      return onFinish(form.getFieldsValue())
    }
  }), [roles, handleSave, handleDoctorSave, onFinish, form]);

  const isLaboratoryLayout = groupType === 'laboratory' && countrySettings?.laboratoryFormLayout?.[countryCode];

  return (
    <div onKeyDown={handleKeyDown} className='body'>
      <Form
        form={form}
        onFinish={onFinish}
        disabled={!isPermittedRole}
        size="large"
        layout="vertical"
        style={{ width: "100%" }}
        scrollToFirstError={{
          block: 'center',
        }}
        onValuesChange={(changedValues) => {
          setVigentExamResultIds(prev => {
            if (prev.size === 0) return prev;
            const next = new Set(prev);
            ['procedures', 'measurements', 'questions'].forEach(type => {
              if (changedValues[type]) {
                Object.keys(changedValues[type]).forEach(id => next.delete(Number(id)));
              }
            });
            return next.size === prev.size ? prev : next;
          });
        }}
      >
        <Row justify="center" className="mb-0 exam-state-wrapper">
          {
            (isGroupedExams && !isLaboratoryLayout) &&
            <Col
              lg={fromExternal ? 24 : inputsBreakpoints()}
              xs={24}
            >
              <Space className="mb-20">
                <p className="mb-0 text-exam-title "><strong>{inputs.name}</strong></p>
                {
                  (inputs.state && !fromExternal) &&
                  <Tag className={inputs.state}>
                    {typesStyles[inputs.state].text}
                  </Tag>
                }
              </Space>
            </Col>
          }
          <Col lg={from === 'consultation' ? 24 : inputsBreakpoints()} xs={24}>
            {(linkedExam && inputs.form_type !== 'rx') && (
              <div className="vigent-exam mt-20 mb-30">
                <LinkedExamBanner variant="linked" examInfo={linkedExam} />
              </div>
            )}
            {
              !linkedExam && vigentExam && (
                (inputs.form_type === 'rx' && enableRx) ? (
                  (inputs.state === 'occupied') && (
                    <VigentExamRx
                      form={form}
                      vigentExam={vigentExam}
                      setIsVinculating={setIsVinculating}
                      setFileList={setFileList}
                      handleConvertFile={executeConvertFile}
                      admissionPatientExamId={admissionPatientExamId}
                      actualExam={inputs}
                    />
                  )
                ) : (
                  canUseVigentExam && (
                    <VigentExam
                      form={form}
                      vigentExam={vigentExam}
                      setFileList={setFileList}
                      handleConvertFile={executeConvertFile}
                      onVigentExamResultsApplied={(ids) => setVigentExamResultIds(new Set(ids))}
                      isInitialized={inputs.state === 'occupied' || !!(fromExternal && toDoctor) || (isSignatureView && !readOnly)}
                      roles={roles}
                      toDoctor={toDoctor}
                      disableForm={isVinculating || !isPermittedRole}
                      readOnly={readOnly}
                    />
                  )
                )
              )
            }
            {
              inputs.form_type === 'interconsultation' &&
              <InterconsultationForm
                fromExternal={fromExternal}
                vigentExamResultIds={vigentExamResultIds}
                onVigentExamFileChange={handleVigentExamFileChange}
                form={form}
                inputs={inputs}
                fileList={fileList}
                setFileList={setFileList}
                loadingFiles={loadingFiles}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                toDoctor={toDoctor}
                status={status}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                isGroupedExams={isGroupedExams}
                enableEcg={enableEcg}
                admissionPatientExamId={admissionPatientExamId}
                setRefresh={setRefresh}
                patient={patient}
                allExams={allExams}
              />
            }
            {questionCalculatorFormTypes.includes(inputs.form_type) && (
              <QuestionCalculatorForm
                readOnly={readOnly}
                fromExternal={fromExternal}
                vigentExamResultIds={vigentExamResultIds}
                onVigentExamFileChange={handleVigentExamFileChange}
                form={form}
                inputs={inputs}
                fileList={fileList}
                setFileList={setFileList}
                loadingFiles={loadingFiles}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                toDoctor={toDoctor}
                status={status}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                isGroupedExams={isGroupedExams}
                enableEcg={enableEcg}
                admissionPatientExamId={admissionPatientExamId}
                setRefresh={setRefresh}
                patient={patient}
                allExams={allExams}
                from={from}
              />
            )}
            {
              (normalFormTypes.includes(inputs.form_type) || (inputs.form_type === 'rx' && !enableRx)) && !isLaboratoryLayout &&
              <NormalForm
                readOnly={readOnly}
                fromExternal={fromExternal}
                vigentExamResultIds={vigentExamResultIds}
                onVigentExamFileChange={handleVigentExamFileChange}
                form={form}
                inputs={inputs}
                fileList={fileList}
                setFileList={setFileList}
                loadingFiles={loadingFiles}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                toDoctor={toDoctor}
                status={status}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                isGroupedExams={isGroupedExams}
                enableEcg={enableEcg}
                admissionPatientExamId={admissionPatientExamId}
                setRefresh={setRefresh}
                patient={patient}
                allExams={allExams}
              />
            }
            {normalFormTypes.includes(inputs.form_type) && isLaboratoryLayout && (
                  <LaboratoryForm
                    readOnly={readOnly}
                    fromExternal={fromExternal}
                    vigentExamResultIds={vigentExamResultIds}
                    form={form}
                    inputs={inputs}
                    patient={patient}
                    toDoctor={toDoctor}
                    disableForm={disableForm || isVinculating || !isPermittedRole}
                    roles={roles}
                    status={status}
                    countrySettings={countrySettings}
                    allExams={allExams}
                    laboratoryCategoryInfo={laboratoryCategoryInfo}
                  />
                )
              }
            {
              inputs.form_type === 'simple_musculoskeletal' &&
              <SimpleMusculoskeletalForm
                readOnly={readOnly}
                fromExternal={fromExternal}
                form={form}
                inputs={inputs}
                fileList={fileList}
                setFileList={setFileList}
                loadingFiles={loadingFiles}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                toDoctor={toDoctor}
                status={status}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                isGroupedExams={isGroupedExams}
                enableEcg={enableEcg}
                admissionPatientExamId={admissionPatientExamId}
                setRefresh={setRefresh}
                patient={patient}
                allExams={allExams}
              />
            }
            {inputs.form_type === 'audiometria' &&
              countrySettings?.audiometry?.formType[countryCode] ===
              'basic' && (
                <BasicAudiometryForm
                  fromExternal={fromExternal}
                  inputs={inputs}
                  form={form}
                  chartRef={ref}
                  fileList={fileList}
                  loadingFiles={loadingFiles}
                  audiometryState={audiometryState}
                  setAudiometry={setAudiometry}
                  disableForm={disableForm || isVinculating || !isPermittedRole}
                  roles={roles}
                  status={status}
                  from={from}
                  setFileList={setFileList}
                  toDoctor={toDoctor}
                  removeDocument={removeDocument}
                  convertFile={handleConvertFile}
                  convertError={convertError}
                  isDICOM={isDICOM}
                  handleConvertFile={executeConvertFile}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                  setShowAudiometryChartRef={(ref) =>
                    (showAudiometryChartRef.current = ref)
                  }
                  countrySettings={countrySettings}
                />
              )}
            {inputs.form_type === 'audiometria' &&
              countrySettings?.audiometry?.formType[countryCode] ===
              'complete' && (
                <CompleteAudiometryForm
                  fromExternal={fromExternal}
                  inputs={inputs}
                  form={form}
                  chartRef={ref}
                  fileList={fileList}
                  loadingFiles={loadingFiles}
                  audiometryState={audiometryState}
                  setAudiometry={setAudiometry}
                  disableForm={disableForm || isVinculating || !isPermittedRole}
                  roles={roles}
                  status={status}
                  from={from}
                  setFileList={setFileList}
                  toDoctor={toDoctor}
                  removeDocument={removeDocument}
                  convertFile={handleConvertFile}
                  convertError={convertError}
                  isDICOM={isDICOM}
                  handleConvertFile={executeConvertFile}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                  setShowAudiometryChartRef={(ref) =>
                    (showAudiometryChartRef.current = ref)
                  }
                  countrySettings={countrySettings}
                />
              )}
            {inputs.form_type === 'audiometria_bilateral' && (
              <BilateralAudiometryForm
                fromExternal={fromExternal}
                inputs={inputs}
                form={form}
                chartRefRE={refRE}
                chartRefLE={refLE}
                fileList={fileList}
                loadingFiles={loadingFiles}
                setAudiometry={setAudiometry}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                status={status}
                from={from}
                setFileList={setFileList}
                toDoctor={toDoctor}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                setShowAudiometryChartRef={(ref) =>
                  (showAudiometryChartRef.current = ref)
                }
                countrySettings={countrySettings}
              />
            )}
            {(inputs.form_type === 'dental') && (
              <OdontogramForm
                ref={odontogramChartRef}
                fromExternal={fromExternal}
                inputs={inputs}
                form={form}
                fileList={fileList}
                loadingFiles={loadingFiles}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                status={status}
                from={from}
                setFileList={setFileList}
                toDoctor={toDoctor}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                odontogramState={odontogramState}
                setOdontogramState={setOdontogramState}
                countrySettings={countrySettings}
              />
            )}
            {inputs.form_type === 'dermatology' && (
              <DermatologyForm
                readOnly={readOnly}
                fromExternal={fromExternal}
                inputs={inputs}
                form={form}
                fileList={fileList}
                loadingFiles={loadingFiles}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                status={status}
                from={from}
                setFileList={setFileList}
                toDoctor={toDoctor}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                dermatologyState={dermatologyState}
                setDermatologyState={setDermatologyState}
                setDermatologyChartRef={(captureFunction) => (dermatologyChartRef.current = captureFunction)}
              />
            )}
            {
              psychologyCalculatorFormTypes.includes(inputs.form_type) &&
              <PsychologyForm
                fromExternal={fromExternal}
                inputs={inputs}
                form={form}
                admissionPatientExamId={admissionPatientExamId}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                psychoState={psychoState}
                setPsycho={setPsycho}
                status={status}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                from={from}
                countryCode={countryCode}
                countrySettings={countrySettings}
              />
            }
            {
              [
                'framingham',
                'framingham_cox'
              ].includes(inputs.form_type) &&
              <CalculatorForm
                admissionPatientId={admissionPatientId}
                inputs={inputs}
                admissionPatientExamId={admissionPatientExamId}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                calculatorState={calculatorState}
                setCalculatorState={setCalculatorState}
                calculatorData={calculatorData}
                setCalculatorData={setCalculatorData}
                from={from}
                countrySettings={countrySettings}
              />
            }
            {
              inputs.form_type === 'drug_test' &&
              <DrugForm
                form={form}
                inputs={inputs}
                admissionPatientExamId={admissionPatientExamId}
                patient={patient}
                drugModal={drugModal}
                setDrugModal={setDrugModal}
                drugState={drugState}
                setDrug={setDrug}
                fetchInputs={fetchInputs}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                fileList={fileList}
                status={status}
                setRefresh={setRefresh}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                fromExternal={fromExternal}
              />
            }
            {
              (inputs.form_type === 'rx' && enableRx) &&
              <RxForm
                form={form}
                inputs={inputs}
                fileList={fileList}
                setFileList={setFileList}
                loadingFiles={loadingFiles}
                disableForm={disableForm || isVinculating || !isPermittedRole}
                roles={roles}
                toDoctor={toDoctor}
                status={status}
                removeDocument={removeDocument}
                convertFile={handleConvertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={executeConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                setRefresh={setRefresh}
                admissionPatientExamId={admissionPatientExamId}
                countrySettings={countrySettings}
                fromExternal={fromExternal}
              />
            }

            {
              ((!isGroupedExams || isLast) && from !== 'expert') &&
              <ExamButtons
                handleSave={handleSave}
                updateLaboratoryExamsButton={updateLaboratoryExamsButton}
                handleDoctorSave={handleDoctorSave}
                disableForm={disableForm}
                readOnly={readOnly}
                isVinculating={isVinculating}
                loading={loading}
                form={form}
                inputs={inputs}
                patient={patient}
                fromExternal={fromExternal}
                isPermittedRole={isPermittedRole}
                isGroupedExams={isGroupedExams}
                handleSaveAllExams={handleSaveAllExams}
                handleFinishAllExams={handleFinishAllExams}
                allExams={allExams}
                getTranslationKey={getTranslationKey}
                downloadReport={downloadReport}
              />
            }
          </Col >
        </Row >
      </Form >
    </div >
  )
});

export default ExamInputs;